import React, { type Dispatch, type FC, type SetStateAction } from 'react';
import { Portal } from 'react-portal';
import { Redirect } from 'react-router-dom';
import type { TrackingProp } from 'react-tracking';
import type { FormValues } from '@ynap/form';
import { useFormField } from '@ynap/form';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { AddressForm } from '../AddressForm';
import { mapAddressValues } from '../AddressForm/utils';
import type { EditAddressAction } from './@types';
import type { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';

type AnyFn = (...args: any[]) => unknown;
export interface EditAddressRouteProps {
    config: Config;
    locale: Locale;
    messages: Messages;
    addresses: Array<FormValues>;
    match: { path: string; url: string };
    editMatch: { path: string; url: string; params: { addressId: string } };
    history: { push: (url: string) => void };
    editAddress?: EditAddressAction;
    editTitle?: string;
    trackUpdateAddress?: AnyFn;
    isCheckoutLayout?: boolean;
    withOverlay?: boolean;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    removeAddressFinder?: (...args: any[]) => any;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
}

const EditAddressRoute: FC<EditAddressRouteProps> = ({
    addresses,
    checkoutAnalytics,
    config,
    editAddress,
    editMatch,
    editTitle,
    hideCountry,
    history,
    isCheckoutLayout,
    locale,
    match,
    messages,
    primaryCategory,
    removeAddressFinder,
    setRemoveAddressFinder,
    tracking,
    trackUpdateAddress,
    withOverlay,
}) => {
    const address = addresses.find((address: FormValues) => address.addressId.toString() === editMatch?.params?.addressId);
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const handleSubmit = (values: FormValues) => {
        if (editAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackUpdateAddress?.();
            return editAddress(mappedValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setAddressIdField({ ...addressIdField, value: addressId });
                history.push(match?.url);
            });
        }

        return null;
    };

    const renderContent = () => (
        <StopPropagation>
            <AddressForm
                touched
                checkoutAnalytics={checkoutAnalytics}
                config={config}
                country={locale.country}
                hideCountry={hideCountry}
                initialValues={address}
                isCheckoutLayout={isCheckoutLayout}
                messages={messages}
                onSubmit={handleSubmit}
                primaryCategory={primaryCategory}
                removeAddressFinder={removeAddressFinder}
                setRemoveAddressFinder={setRemoveAddressFinder}
                tracking={tracking}
            />
        </StopPropagation>
    );

    if (address) {
        if (withOverlay) {
            return (
                <Portal>
                    <Overlay onClose={() => history.push(match.url)} title={editTitle}>
                        {renderContent()}
                    </Overlay>
                </Portal>
            );
        }

        return renderContent();
    }

    return <Redirect to={match.url} />;
};

export default EditAddressRoute;
